import React from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Database, CheckCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react'
import GalleryFilters from '../components/filters/GalleryFilters'
import GalleryList from '../components/list/GalleryList'
import BulkSelectionBar from '../components/bulk/BulkSelectionBar'
import FilterChips from '../components/filters/FilterChips'
import { useTags } from '../hooks/useGalleryTags'
import { useServices } from '../hooks/useServices'
import { useJobDetailsSyncStatus } from '../hooks/useJobDetailsSyncStatus'
import { useBulkSyncGalleries } from '../hooks/useBulkSyncGalleries'
import { serializeParams, type GalleryUrlParams } from '../utils/urlParams'
import { useGalleries } from '../hooks/useGalleries'
import { bulkSetPublic, bulkUpdateStatus } from '../api/galleryApi'
import useGalleryCovers from '../hooks/useGalleryCovers'

export default function GalleryManagementV2() {
  const navigate = useNavigate()
  const [sp, setSp] = useSearchParams()
  const params: GalleryUrlParams = {
    page: Number(sp.get('page') || 1),
    pageSize: Number(sp.get('pageSize') || 24),
    search: sp.get('q') || '',
    regions: sp.get('regions')?.split(',').filter(Boolean) || [],
    statuses: sp.get('statuses')?.split(',').filter(Boolean) || [],
    tags: sp.get('tags')?.split(',').filter(Boolean) || [],
    services: sp.get('services')?.split(',').filter(Boolean) || [],
    needsSync: sp.get('needsSync') === 'true',
    sortBy: (sp.get('sortBy') as any) || 'created_on',
    sortDir: (sp.get('sortDir') as any) || 'desc',
    view: (sp.get('view') as any) || 'grid',
  }

  const { data, isLoading, hasNextPage, hasPrevPage, totalPages, prefetchPage } = useGalleries({
    page: params.page || 1,
    pageSize: params.pageSize || 24,
    searchTerm: params.search,
    regions: params.regions,
    statuses: params.statuses,
    tags: params.tags,
    services: params.services,
    needsJobDetailsSync: params.needsSync,
    sortBy: params.sortBy || 'created_on',
    sortDir: params.sortDir || 'desc',
  })

  const galleries = data?.galleries || []
  const { coverById, isLoading: coversLoading } = useGalleryCovers(galleries)
  const { data: syncStatus, isLoading: syncStatusLoading } = useJobDetailsSyncStatus()
  const { runBulkSync, isRunning: isBulkSyncing, progress: bulkProgress } = useBulkSyncGalleries()

  const updateParams = (next: Partial<GalleryUrlParams>, options?: { resetPage?: boolean }) => {
    const merged = { ...params, ...next }
    if (options?.resetPage && next.page == null) merged.page = 1
    setSp(serializeParams(merged), { replace: true })
  }

  const [selected, setSelected] = React.useState<Set<number>>(new Set())
  const toggleSelect = (id: number) => setSelected(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s
  })
  const clearSelection = () => setSelected(new Set())

  const doBulk = async (action: 'approve' | 'reject' | 'public' | 'private') => {
    const ids = Array.from(selected)
    if (!ids.length) return
    if (action === 'approve') await bulkUpdateStatus(ids, 'approved')
    if (action === 'reject') await bulkUpdateStatus(ids, 'rejected')
    if (action === 'public') await bulkSetPublic(ids, true)
    if (action === 'private') await bulkSetPublic(ids, false)
    clearSelection()
    // Force refetch by nudging the same params
    setSp(serializeParams({ ...params }), { replace: true })
  }

  // Tag and service names for chips
  const { data: tags = [] } = useTags()
  const tagNameById = React.useMemo(() => new Map(tags.map(t => [String(t.id), t.name])), [tags])

  const { data: services = [] } = useServices()
  const serviceNameById = React.useMemo(() => new Map(services.map(s => [s.id, s.title])), [services])

  const chips = React.useMemo(() => {
    const out: { key: string; label: string }[] = []
    if (params.search) out.push({ key: 'q', label: `Search: ${params.search}` })
    for (const r of params.regions || []) out.push({ key: `region:${r}`, label: `Region: ${r}` })
    for (const s of params.statuses || []) out.push({ key: `status:${s}`, label: `Status: ${s.replace(/_/g, ' ')}` })
    for (const tid of params.tags || []) out.push({ key: `tag:${tid}`, label: `Tag: ${tagNameById.get(tid) || `#${tid}`}` })
    for (const sid of params.services || []) out.push({ key: `service:${sid}`, label: `Service: ${serviceNameById.get(sid) || sid}` })
    return out
  }, [params.search, params.regions, params.statuses, params.tags, params.services, tagNameById, serviceNameById])

  const handleRemoveChip = (key: string) => {
    if (key === 'q') return updateParams({ search: '' }, { resetPage: true })
    if (key.startsWith('region:')) {
      const val = key.slice('region:'.length)
      return updateParams({ regions: (params.regions || []).filter(r => r !== val) }, { resetPage: true })
    }
    if (key.startsWith('status:')) {
      const val = key.slice('status:'.length)
      return updateParams({ statuses: (params.statuses || []).filter(s => s !== val) }, { resetPage: true })
    }
    if (key.startsWith('tag:')) {
      const val = key.slice('tag:'.length)
      return updateParams({ tags: (params.tags || []).filter(t => t !== val) }, { resetPage: true })
    }
    if (key.startsWith('service:')) {
      const val = key.slice('service:'.length)
      return updateParams({ services: (params.services || []).filter(s => s !== val) }, { resetPage: true })
    }
  }

  const handleClearChips = () => updateParams({ search: '', regions: [], statuses: [], tags: [], services: [] }, { resetPage: true })

  // Build page numbers with a wider window around current (defaults to ±2)
  const currentPage = params.page || 1
  const buildPageItems = (total: number, current: number, window: number = 2) => {
    const items: (number | 'ellipsis')[] = []
    const showAllThreshold = 2 * window + 5 // e.g., window=2 -> 9
    if (total <= showAllThreshold) {
      for (let i = 1; i <= total; i++) items.push(i)
      return items
    }
    const add = (v: number | 'ellipsis') => items.push(v)
    add(1)
    const start = Math.max(2, current - window)
    const end = Math.min(total - 1, current + window)
    if (start > 2) add('ellipsis')
    for (let i = start; i <= end; i++) add(i)
    if (end < total - 1) add('ellipsis')
    add(total)
    return items
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
        <h1 className='text-2xl font-bold'>Gallery Management (V2)</h1>
        <Button variant='outline' onClick={() => navigate('/media')} className="w-full sm:w-auto">Back</Button>
      </div>

      {/* Job Details Sync Status Widget */}
      <Card className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              <Database className="h-4 w-4" />
              <span>Job Details Sync Status</span>
            </div>

            {syncStatusLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                Loading...
              </div>
            ) : syncStatus ? (
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-800 gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" />
                  {syncStatus.synced} synced
                </Badge>

                {syncStatus.notSynced > 0 && (
                  <Badge
                    variant="outline"
                    className={`gap-1.5 cursor-pointer transition-all hover:scale-105 ${
                      params.needsSync
                        ? 'bg-orange-100 text-orange-800 border-orange-400 ring-2 ring-orange-300 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-600 dark:ring-orange-700'
                        : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 dark:bg-orange-950/50 dark:text-orange-400 dark:border-orange-800 dark:hover:bg-orange-900/50'
                    }`}
                    onClick={() => updateParams({ needsSync: !params.needsSync }, { resetPage: true })}
                    title={params.needsSync ? 'Click to clear filter' : 'Click to show only galleries needing sync'}
                  >
                    <Clock className="h-3.5 w-3.5" />
                    {syncStatus.notSynced} need sync
                  </Badge>
                )}

                {syncStatus.noJobNumber > 0 && (
                  <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700 gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {syncStatus.noJobNumber} no job#
                  </Badge>
                )}

                <span className="text-xs text-muted-foreground">
                  of {syncStatus.total} galleries
                </span>

                {params.needsSync && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-orange-600 hover:text-orange-800 hover:bg-orange-100"
                    onClick={() => updateParams({ needsSync: false }, { resetPage: true })}
                  >
                    Clear filter
                  </Button>
                )}

                {/* Bulk Sync Button */}
                {syncStatus && syncStatus.notSynced > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 ml-2"
                    onClick={runBulkSync}
                    disabled={isBulkSyncing}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${isBulkSyncing ? 'animate-spin' : ''}`} />
                    {isBulkSyncing
                      ? `Syncing ${bulkProgress.completed}/${bulkProgress.total}...`
                      : `Sync All (${syncStatus.notSynced})`
                    }
                  </Button>
                )}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">Unable to load status</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className='pt-6'>
          <GalleryFilters
            search={params.search || ''}
            regions={params.regions || []}
            statuses={params.statuses || []}
            tags={params.tags || []}
            services={params.services || []}
            sortBy={(params.sortBy || 'created_on') as any}
            sortDir={(params.sortDir || 'desc') as any}
            onChange={(next) => updateParams(next as any, { resetPage: true })}
          />
          <div className='mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
            <FilterChips chips={chips} onClear={handleClearChips} onRemove={handleRemoveChip} />
            <div className='flex items-center gap-2 shrink-0'>
              <Button
                variant={params.view === 'grid' ? 'default' : 'outline'}
                size='sm'
                className="flex-1 sm:flex-none"
                onClick={() => updateParams({ view: 'grid' })}
              >Grid</Button>
              <Button
                variant={params.view === 'table' ? 'default' : 'outline'}
                size='sm'
                className="flex-1 sm:flex-none"
                onClick={() => updateParams({ view: 'table' })}
              >Table</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Results {data ? `(${data.totalCount})` : ''}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <GalleryList
            items={galleries}
            view={(params.view as any) || 'grid'}
            onSelect={(id) => navigate(`/media/galleries/${id}`)}
            loading={isLoading || coversLoading}
            selectedIds={selected}
            onToggleSelect={toggleSelect}
            coverById={coverById}
          />

          <div className='flex flex-col sm:flex-row items-center justify-between gap-4'>
            <div className='flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-sm text-muted-foreground w-full sm:w-auto'>
              <div className='whitespace-nowrap'>
                Page {params.page || 1} / {totalPages}
              </div>
              <div className='flex items-center gap-2'>
                <span className='whitespace-nowrap'>Rows per page</span>
                <Select
                  value={String(params.pageSize || 24)}
                  onValueChange={(v) => updateParams({ pageSize: Number(v), page: 1 })}
                >
                  <SelectTrigger className='w-[80px] h-8'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='12'>12</SelectItem>
                    <SelectItem value='24'>24</SelectItem>
                    <SelectItem value='48'>48</SelectItem>
                    <SelectItem value='96'>96</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className='flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar pb-1 sm:pb-0 w-full sm:w-auto justify-center sm:justify-end'>
              <Button
                variant='outline'
                size='sm'
                disabled={!hasPrevPage}
                onMouseEnter={() => hasPrevPage && prefetchPage(currentPage - 1)}
                onClick={() => updateParams({ page: currentPage - 1 })}
                className='h-8'
              >Prev</Button>

              <div className="flex items-center gap-1">
                {buildPageItems(totalPages || 1, currentPage, 1).map((it, idx) => (
                  it === 'ellipsis' ? (
                    <span key={`e-${idx}`} className='px-0.5 text-muted-foreground'>…</span>
                  ) : (
                    <Button
                      key={it}
                      variant={it === currentPage ? 'default' : 'outline'}
                      size='sm'
                      aria-current={it === currentPage ? 'page' : undefined}
                      onMouseEnter={() => prefetchPage(it as number)}
                      onClick={() => updateParams({ page: it as number })}
                      className='h-8 w-8 p-0'
                    >{it}</Button>
                  )
                ))}
              </div>

              <Button
                variant='outline'
                size='sm'
                disabled={!hasNextPage}
                onMouseEnter={() => hasNextPage && prefetchPage(currentPage + 1)}
                onClick={() => updateParams({ page: currentPage + 1 })}
                className='h-8'
              >Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <BulkSelectionBar
        count={selected.size}
        onApprove={() => doBulk('approve')}
        onReject={() => doBulk('reject')}
        onMakePublic={() => doBulk('public')}
        onMakePrivate={() => doBulk('private')}
        onClear={clearSelection}
      />
    </div>
  )
}
